/**
 * Setting an authenticator up — the same three steps on Profile and in the
 * middle of a sign-in an organization requires it for: scan (or type the
 * secret), type the code it shows, keep the ten recovery codes. `start` and
 * `enable` are the two calls, which differ only in where they go.
 *
 * Drawn as the redesign's enrolment steps (§37.5): numbered kickers, teal for
 * the step at hand and grey with a check once done, a done step folded to its
 * kicker, 32 between two steps. The QR is the code itself at 160 on white, the
 * key is a copyable read-only field, the codes sit in their own frame with
 * « Copy », and « I saved them » closes it.
 */
import { useEffect, useState } from 'react';
import type React from 'react';
import { color } from '../../theme/tokens';
import { PrimaryButton } from '../../components/primitives';
import { QrCode } from '../../components/QrCode';
import { ApiError } from '../../lib/api';
import { CodeInput, codeRefusal, CopyableKey, RecoveryCodes, StepKicker } from './two-factor-parts';

const STEP: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 12 };

export function TwoFactorSetup({
  start,
  enable,
  onDone
}: {
  start: () => Promise<{ secret: string; uri: string }>;
  enable: (code: string) => Promise<string[]>;
  onDone: () => void;
}): React.JSX.Element {
  const [setup, setSetup] = useState<{ secret: string; uri: string } | null>(null);
  const [code, setCode] = useState('');
  const [codes, setCodes] = useState<string[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void start()
      .then(setSetup)
      .catch((failure: unknown) => setError(failure instanceof ApiError ? failure.message : 'That did not work.'));
  }, [start]);

  if (codes) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 32 }}>
        <StepKicker done>1 · Scan</StepKicker>
        <StepKicker done>2 · Confirm</StepKicker>
        <div style={STEP}>
          <StepKicker>3 · Save the codes</StepKicker>
          <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.55, color: color.inkBody }}>
            Two-factor is on. Keep these recovery codes somewhere safe — each works once if you lose your phone.
          </p>
          <RecoveryCodes codes={codes} />
          <div style={{ marginTop: 8 }}>
            <PrimaryButton tone="ink" size="lg" full onClick={onDone}>
              I saved them
            </PrimaryButton>
          </div>
        </div>
      </div>
    );
  }

  const send = async (typed: string): Promise<void> => {
    if (busy || typed.length !== 6) return;
    setError(null);
    setBusy(true);
    try {
      setCodes(await enable(typed));
    } catch (failure) {
      setError(codeRefusal(failure, 'enrol'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        void send(code);
      }}
      style={{ display: 'flex', flexDirection: 'column', gap: 32 }}
    >
      <div style={STEP}>
        <StepKicker>1 · Scan</StepKicker>
        <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.55, color: color.inkBody }}>
          Scan this with an authenticator app — 1Password, Google Authenticator, Authy — then type the code it shows.
        </p>
        {setup && (
          <>
            <div
              style={{
                width: 160,
                height: 160,
                boxSizing: 'border-box',
                border: `1px solid ${color.line}`,
                borderRadius: 8,
                background: '#FFFFFF',
                overflow: 'hidden'
              }}
            >
              <QrCode text={setup.uri} size={158} />
            </div>
            <span style={{ fontSize: 13, color: color.inkSubtle }}>Or type this key:</span>
            <CopyableKey label="The key, to type into the app" value={setup.secret} />
          </>
        )}
      </div>
      <div style={STEP}>
        <StepKicker>2 · Confirm</StepKicker>
        <div>
          <label htmlFor="tfa-setup-code" style={{ display: 'block', fontSize: 13, fontWeight: 500, color: color.ink, marginBottom: 6 }}>
            Code
          </label>
          <CodeInput
            id="tfa-setup-code"
            value={code}
            onChange={setCode}
            onComplete={(typed) => void send(typed)}
            invalid={Boolean(error)}
            readOnly={busy}
            describedBy={error ? 'tfa-setup-error' : undefined}
          />
          {error && (
            <p id="tfa-setup-error" role="alert" style={{ margin: '6px 0 0', fontSize: 13, lineHeight: 1.4, color: color.badText }}>
              {error}
            </p>
          )}
        </div>
        <div style={{ marginTop: 12 }}>
          <PrimaryButton
            type="submit"
            tone="ink"
            size="lg"
            full
            busy={busy}
            reason={code.length < 6 && !busy ? 'Type the six digits first' : undefined}
          >
            {busy ? 'Turning on' : 'Turn it on'}
          </PrimaryButton>
        </div>
      </div>
    </form>
  );
}
